import "./CompanyInventory.css";
import { usePermissions } from "./PermissionContext";
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const API_URL = "/api";
const PAGE_SIZE = 10;

const initialMovement = {
  warehouse_id: "",
  product_id: "",
  variant_id: "",
  movement_type: "entry",
  quantity: "",
  concept: "",
  note: "",
  lot_number: "",
  expiration_date: "",
};

function getHeaders() {
  return {
    headers: {
      Authorization: `Bearer ${localStorage.getItem(
        "company_token"
      )}`,
    },
  };
}

const formatQuantity = (value) =>
  new Intl.NumberFormat("es-CO", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(Number(value || 0));

const formatMoney = (value) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return "Sin vencimiento";

  return new Date(
    `${value}T00:00:00`
  ).toLocaleDateString("es-CO");
};

function Pagination({ total, offset, onPageChange }) {
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(
    1,
    Math.ceil(total / PAGE_SIZE)
  );

  return (
    <div className="inventory-pagination">
      <span>
        Página {currentPage} de {totalPages} · {total} registros
      </span>

      <div>
        <button
          type="button"
          disabled={offset === 0}
          onClick={() =>
            onPageChange(Math.max(0, offset - PAGE_SIZE))
          }
        >
          Anterior
        </button>

        <button
          type="button"
          disabled={offset + PAGE_SIZE >= total}
          onClick={() => onPageChange(offset + PAGE_SIZE)}
        >
          Siguiente
        </button>
      </div>
    </div>
  );
}

function CompanyInventory() {
  const { can } = usePermissions();
  const canManageInventory = can("inventory.manage");

  const [inventory, setInventory] = useState([]);
  const [inventoryTotal, setInventoryTotal] = useState(0);
  const [inventoryOffset, setInventoryOffset] = useState(0);
  const [inventorySearch, setInventorySearch] = useState("");
  const [onlyLowStock, setOnlyLowStock] = useState(false);

  const [movements, setMovements] = useState([]);
  const [movementsTotal, setMovementsTotal] = useState(0);
  const [movementsOffset, setMovementsOffset] = useState(0);
  const [movementSearch, setMovementSearch] = useState("");
  const [movementTypeFilter, setMovementTypeFilter] =
    useState("");
  const [movementProductFilter, setMovementProductFilter] =
    useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [lots, setLots] = useState([]);
  const [lotsTotal, setLotsTotal] = useState(0);
  const [lotsOffset, setLotsOffset] = useState(0);
  const [lotExpirationFilter, setLotExpirationFilter] =
    useState("");

  const [productOptions, setProductOptions] = useState([]);
  const [productVariants, setProductVariants] = useState([]);
  const [loadingProductVariants, setLoadingProductVariants] =
    useState(false);
  const [movementData, setMovementData] =
    useState(initialMovement);
  const [loading, setLoading] = useState(true);

  const [alertSummary, setAlertSummary] = useState({
    total_alerts: 0,
    exhausted: 0,
    critical: 0,
    low: 0,
    expired: 0,
    expiring_soon: 0,
  });

  const handleWarehouseChange = (event) => {
    setSelectedWarehouseId(event.target.value);
    setInventoryOffset(0);
    setMovementsOffset(0);
    setLotsOffset(0);
  };

  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouseId, setSelectedWarehouseId] =
    useState("");

  const fetchInventory = useCallback(async () => {
    const response = await axios.get(
      `${API_URL}/inventory/me`,
      {
        ...getHeaders(),
        params: {
          limit: PAGE_SIZE,
          offset: inventoryOffset,
          search: inventorySearch || undefined,
          only_low_stock: onlyLowStock,
          warehouse_id: selectedWarehouseId || undefined,
        },
      }
    );

    setInventory(response.data.items);
    setInventoryTotal(response.data.total);
  }, [
    inventoryOffset,
    inventorySearch,
    onlyLowStock,
    selectedWarehouseId,
  ]);

  const fetchWarehouses = useCallback(async () => {
    const response = await axios.get(
      `${API_URL}/warehouses/me`,
      {
        ...getHeaders(),
        params: { active_only: true },
      }
    );

    setWarehouses(response.data);
  }, []);

  useEffect(() => {
    fetchWarehouses().catch((error) => {
      toast.error(
        error.response?.data?.detail ||
        "Error cargando las bodegas"
      );
    });
  }, [fetchWarehouses]);

  const fetchAlertSummary = useCallback(async () => {
    const response = await axios.get(
      `${API_URL}/inventory/me/alerts/summary`,
      {
        ...getHeaders(),
        params: {
          warehouse_id: selectedWarehouseId || undefined,
        },
      }
    );

    setAlertSummary(response.data);
  }, [selectedWarehouseId]);

  const fetchMovements = useCallback(async () => {
    const response = await axios.get(
      `${API_URL}/inventory/me/movements`,
      {
        ...getHeaders(),
        params: {
          limit: PAGE_SIZE,
          offset: movementsOffset,
          search: movementSearch || undefined,
          product_id: movementProductFilter || undefined,
          movement_type: movementTypeFilter || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          warehouse_id: selectedWarehouseId || undefined,
        },
      }
    );

    setMovements(response.data.items);
    setMovementsTotal(response.data.total);
  }, [
    movementsOffset,
    movementSearch,
    movementProductFilter,
    movementTypeFilter,
    dateFrom,
    dateTo,
    selectedWarehouseId,
  ]);

  const fetchLots = useCallback(async () => {
    const response = await axios.get(
      `${API_URL}/inventory/me/lots`,
      {
        ...getHeaders(),
        params: {
          limit: PAGE_SIZE,
          offset: lotsOffset,
          expiration_status:
            lotExpirationFilter || undefined,
          warehouse_id: selectedWarehouseId || undefined,
        },
      }
    );

    setLots(response.data.items);
    setLotsTotal(response.data.total);
  }, [lotsOffset, lotExpirationFilter, selectedWarehouseId]);

  const fetchProductOptions = useCallback(async () => {
    const response = await axios.get(
      `${API_URL}/products/me`,
      getHeaders()
    );

    setProductOptions(response.data);
  }, []);

  useEffect(() => {
    fetchInventory()
      .catch((error) => {
        toast.error(
          error.response?.data?.detail ||
          "Error cargando el inventario"
        );
      })
      .finally(() => setLoading(false));
  }, [fetchInventory]);

  useEffect(() => {
    const productId = Number(movementData.product_id);

    if (!productId) {
      setProductVariants([]);
      setLoadingProductVariants(false);
      return;
    }

    let cancelled = false;

    const fetchProductVariants = async () => {
      setLoadingProductVariants(true);

      try {
        const response = await axios.get(
          `${API_URL}/products/me/${productId}/variants`,
          {
            ...getHeaders(),
            params: {
              only_active: true,
            },
          }
        );

        if (!cancelled) {
          setProductVariants(response.data);
        }
      } catch (error) {
        if (!cancelled) {
          setProductVariants([]);

          toast.error(
            error.response?.data?.detail ||
            "No fue posible cargar las variantes del producto."
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingProductVariants(false);
        }
      }
    };

    fetchProductVariants();

    return () => {
      cancelled = true;
    };
  }, [movementData.product_id]);

  useEffect(() => {
    fetchAlertSummary().catch((error) => {
      toast.error(
        error.response?.data?.detail ||
        "Error cargando alertas de inventario"
      );
    });
  }, [fetchAlertSummary]);

  useEffect(() => {
    fetchMovements().catch((error) => {
      toast.error(
        error.response?.data?.detail ||
        "Error cargando movimientos"
      );
    });
  }, [fetchMovements]);

  useEffect(() => {
    fetchLots().catch((error) => {
      toast.error(
        error.response?.data?.detail ||
        "Error cargando lotes de inventario"
      );
    });
  }, [fetchLots]);

  useEffect(() => {
    fetchProductOptions().catch((error) => {
      toast.error(
        error.response?.data?.detail ||
        "Error cargando productos"
      );
    });
  }, [fetchProductOptions]);

  const handleMovementChange = (event) => {
    const { name, value } = event.target;

    setMovementData((current) => ({
      ...current,
      [name]: value,
      ...(name === "product_id" ? { variant_id: "" } : {}),
    }));
  };

  const showStockAlerts = () => {
    setInventorySearch("");
    setOnlyLowStock(true);
    setInventoryOffset(0);
  };

  const showLotAlerts = (status) => {
    setLotExpirationFilter(status);
    setLotsOffset(0);
  };

  const getLotStatusLabel = (status) => {
    const labels = {
      expired: "Vencido",
      expiring_soon: "Próximo a vencer",
      valid: "Vigente",
      without_expiration: "Sin vencimiento",
    };

    return labels[status] || "Sin estado";
  };

  const createMovement = async (event) => {
    event.preventDefault();

    if (!canManageInventory) {
      toast.error(
        "No tienes permiso para registrar movimientos de inventario."
      );
      return;
    }

    if (!movementData.warehouse_id) {
      toast.error("Selecciona la bodega para el movimiento.");
      return;
    }

    try {
      await axios.post(
        `${API_URL}/inventory/me/movements`,
        {
          product_id: Number(movementData.product_id),
          variant_id: movementData.variant_id
            ? Number(movementData.variant_id)
            : null,
          movement_type: movementData.movement_type,
          quantity: Number(movementData.quantity),
          concept: movementData.concept,
          note: movementData.note || null,
          lot_number:
            movementData.movement_type === "entry"
              ? movementData.lot_number || null
              : null,
          expiration_date:
            movementData.movement_type === "entry"
              ? movementData.expiration_date || null
              : null,
          warehouse_id: Number(movementData.warehouse_id),
        },
        getHeaders()
      );

      toast.success(
        movementData.movement_type === "entry"
          ? "Entrada de inventario registrada."
          : "Salida de inventario registrada."
      );

      setMovementData(initialMovement);
      setInventoryOffset(0);
      setMovementsOffset(0);
      setLotsOffset(0);

      await Promise.all([
        fetchInventory(),
        fetchMovements(),
        fetchLots(),
        fetchAlertSummary(),
      ]);
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
        "No fue posible registrar el movimiento"
      );
    }
  };

  const selectedProduct = productOptions.find(
    (product) =>
      product.id === Number(movementData.product_id)
  );

  const selectedVariant = productVariants.find(
    (variant) =>
      variant.id === Number(movementData.variant_id)
  );

  const productNames = useMemo(
    () =>
      productOptions.reduce((result, product) => {
        result[product.id] = product.name;
        return result;
      }, {}),
    [productOptions]
  );

  const resetMovementFilters = () => {
    setMovementSearch("");
    setMovementTypeFilter("");
    setMovementProductFilter("");
    setDateFrom("");
    setDateTo("");
    setMovementsOffset(0);
  };

  if (loading) {
    return <h2>Cargando inventario...</h2>;
  }

  return (
    <div className="company-inventory">
      <div className="inventory-heading">
        <div>
          <h1>Inventario</h1>
          <p>
            Registra entradas y salidas, consulta existencias,
            lotes y alertas de inventario.
          </p>
        </div>
        <label className="inventory-warehouse-selector">
          <span>Bodega consultada</span>

          <select
            value={selectedWarehouseId}
            onChange={handleWarehouseChange}
          >
            <option value="">Todas las bodegas</option>

            {warehouses.map((warehouse) => (
              <option key={warehouse.id} value={warehouse.id}>
                {warehouse.name}
                {warehouse.is_default ? " · Principal" : ""}
              </option>
            ))}
          </select>
        </label>
      </div>

      <section className="inventory-summary-grid">
        <article className="inventory-summary-card stock-total">
          <span>Productos encontrados</span>
          <strong>{inventoryTotal}</strong>
          <small>
            {onlyLowStock
              ? "Mostrando productos con alerta"
              : "Según los filtros aplicados"}
          </small>
        </article>

        <button
          type="button"
          className="inventory-summary-card alert-exhausted"
          onClick={showStockAlerts}
        >
          <span>Agotados</span>
          <strong>{alertSummary.exhausted}</strong>
          <small>Stock en cero o negativo</small>
        </button>

        <button
          type="button"
          className="inventory-summary-card alert-critical"
          onClick={showStockAlerts}
        >
          <span>Stock crítico</span>
          <strong>{alertSummary.critical}</strong>
          <small>Requiere reposición prioritaria</small>
        </button>

        <button
          type="button"
          className="inventory-summary-card low-stock"
          onClick={showStockAlerts}
        >
          <span>Stock bajo</span>
          <strong>{alertSummary.low}</strong>
          <small>
            {alertSummary.total_alerts} alertas activas
          </small>
        </button>

        <button
          type="button"
          className="inventory-summary-card alert-expired"
          onClick={() => showLotAlerts("expired")}
        >
          <span>Lotes vencidos</span>
          <strong>{alertSummary.expired}</strong>
          <small>No deben venderse</small>
        </button>

        <button
          type="button"
          className="inventory-summary-card alert-expiring"
          onClick={() => showLotAlerts("expiring_soon")}
        >
          <span>Próximos a vencer</span>
          <strong>{alertSummary.expiring_soon}</strong>
          <small>Vencen dentro de 30 días</small>
        </button>
      </section>

      <section className="inventory-card movement-form-card">
        <h2>Registrar movimiento</h2>
        <p>
          Registra compras, devoluciones, productos dañados u otras
          variaciones del inventario.
        </p>

        {canManageInventory ? (
          <form onSubmit={createMovement}>
            <div className="inventory-form-grid">
              <div className="inventory-field">
                <label htmlFor="movement_warehouse_id">Bodega</label>

                <select
                  id="movement_warehouse_id"
                  name="warehouse_id"
                  value={movementData.warehouse_id}
                  onChange={handleMovementChange}
                  required
                >
                  <option value="">Selecciona una bodega</option>

                  {warehouses.map((warehouse) => (
                    <option key={warehouse.id} value={warehouse.id}>
                      {warehouse.name}
                      {warehouse.is_default ? " · Principal" : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="inventory-field inventory-field-wide">
                <label htmlFor="product_id">Producto</label>

                <select
                  id="product_id"
                  name="product_id"
                  value={movementData.product_id}
                  onChange={handleMovementChange}
                  required
                >
                  <option value="">
                    Selecciona un producto
                  </option>

                  {productOptions
                    .filter((product) => product.is_active)
                    .map((product) => (
                      <option
                        key={product.id}
                        value={product.id}
                      >
                        {product.sku} - {product.name}
                      </option>
                    ))}
                </select>

                {selectedProduct && (
                  <small className="selected-stock">
                    Producto seleccionado:{" "}
                    <strong>{selectedProduct.name}</strong>
                  </small>
                )}
              </div>

              <div className="inventory-field">
                {movementData.product_id && (
                  <div className="inventory-field inventory-field-wide">
                    <label htmlFor="variant_id">Variante</label>

                    <select
                      id="variant_id"
                      name="variant_id"
                      value={movementData.variant_id}
                      onChange={handleMovementChange}
                      disabled={loadingProductVariants}
                    >
                      <option value="">
                        {loadingProductVariants
                          ? "Cargando variantes..."
                          : "Producto base (sin variante)"}
                      </option>

                      {productVariants.map((variant) => (
                        <option key={variant.id} value={variant.id}>
                          {variant.sku} - {variant.name}
                          {variant.sale_price
                            ? ` · ${formatMoney(variant.sale_price)}`
                            : ""}
                          {` · Stock: ${formatQuantity(variant.stock)}`}
                        </option>
                      ))}
                    </select>

                    {loadingProductVariants ? (
                      <small className="selected-stock">
                        Consultando variantes disponibles...
                      </small>
                    ) : productVariants.length === 0 ? (
                      <small className="selected-stock">
                        Este producto no tiene variantes activas. El movimiento se
                        registrará sobre el producto base.
                      </small>
                    ) : selectedVariant ? (
                      <small className="selected-stock">
                        Variante seleccionada:{" "}
                        <strong>
                          {selectedVariant.name} ({selectedVariant.sku})
                        </strong>
                        {" · "}Stock actual:{" "}
                        <strong>
                          {formatQuantity(selectedVariant.stock)}
                        </strong>
                      </small>
                    ) : (
                      <small className="selected-stock">
                        Si no eliges una variante, el movimiento afectará el stock
                        del producto base.
                      </small>
                    )}
                  </div>
                )}
                <label htmlFor="movement_type">Tipo</label>

                <select
                  id="movement_type"
                  name="movement_type"
                  value={movementData.movement_type}
                  onChange={handleMovementChange}
                >
                  <option value="entry">Entrada</option>
                  <option value="exit">Salida</option>
                </select>
              </div>

              <div className="inventory-field">
                <label htmlFor="quantity">Cantidad</label>

                <input
                  type="number"
                  id="quantity"
                  name="quantity"
                  min="0.001"
                  step="0.001"
                  placeholder="0"
                  value={movementData.quantity}
                  onChange={handleMovementChange}
                  required
                />
              </div>

              <div className="inventory-field inventory-field-wide">
                <label htmlFor="concept">Concepto</label>

                <input
                  type="text"
                  id="concept"
                  name="concept"
                  placeholder="Ejemplo: Producto dañado"
                  value={movementData.concept}
                  onChange={handleMovementChange}
                  required
                />
              </div>
              {movementData.movement_type === "entry" && (
                <>
                  <div className="inventory-field">
                    <label htmlFor="lot_number">
                      Número de lote
                    </label>

                    <input
                      type="text"
                      id="lot_number"
                      name="lot_number"
                      maxLength="100"
                      placeholder="Ejemplo: L-2026-001"
                      value={movementData.lot_number}
                      onChange={handleMovementChange}
                    />
                  </div>

                  <div className="inventory-field">
                    <label htmlFor="expiration_date">
                      Fecha de vencimiento
                    </label>

                    <input
                      type="date"
                      id="expiration_date"
                      name="expiration_date"
                      value={movementData.expiration_date}
                      onChange={handleMovementChange}
                    />
                  </div>
                </>
              )}
            </div>

            <div className="inventory-field">
              <label htmlFor="note">Nota adicional</label>

              <textarea
                id="note"
                name="note"
                rows="3"
                placeholder="Información adicional (opcional)"
                value={movementData.note}
                onChange={handleMovementChange}
              />
            </div>

            <button
              type="submit"
              className={
                movementData.movement_type === "entry"
                  ? "btn-register-entry"
                  : "btn-register-exit"
              }
            >
              {movementData.movement_type === "entry"
                ? "Registrar entrada"
                : "Registrar salida"}
            </button>
          </form>
        ) : (
          <div className="inventory-readonly-message">
            No tienes permiso para registrar entradas, salidas o
            ajustes de inventario.
          </div>
        )}
      </section>

      <section className="inventory-card stock-list-card">
        <div className="section-title">
          <div>
            <h2>Stock actual por producto</h2>
            <p>
              Busca por nombre o SKU. Los productos con alerta
              aparecen resaltados según su nivel de urgencia.
            </p>
          </div>

          <span className="stock-alert-counter">
            {alertSummary.total_alerts} alertas activas
          </span>
        </div>

        <div className="inventory-filters">
          <input
            type="search"
            placeholder="Buscar producto o SKU"
            value={inventorySearch}
            onChange={(event) => {
              setInventorySearch(event.target.value);
              setInventoryOffset(0);
            }}
          />

          <label className="low-stock-filter">
            <input
              type="checkbox"
              checked={onlyLowStock}
              onChange={(event) => {
                setOnlyLowStock(event.target.checked);
                setInventoryOffset(0);
              }}
            />
            Solo productos con alerta
          </label>
        </div>

        <div className="inventory-table-wrapper">
          <table className="inventory-table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Producto</th>
                <th>Categoría</th>
                <th>Stock actual</th>
                <th>Stock mínimo</th>
                <th>Faltante</th>
                <th>Estado</th>
              </tr>
            </thead>

            <tbody>
              {inventory.length === 0 ? (
                <tr>
                  <td colSpan="7" className="empty-inventory">
                    No hay productos para los filtros seleccionados.
                  </td>
                </tr>
              ) : (
                inventory.map((product) => (
                  <tr
                    key={`${product.id}:${product.variant_id ?? "base"}`}
                    className={
                      product.alert_status
                        ? "low-stock-row"
                        : ""
                    }
                  >
                    <td>{product.sku}</td>
                    <td>{product.name}</td>
                    <td>
                      {product.category || "Sin categoría"}
                    </td>

                    <td>
                      <strong
                        className={
                          product.alert_status
                            ? "stock-low"
                            : "stock-normal"
                        }
                      >
                        {formatQuantity(product.current_stock)}
                      </strong>
                    </td>

                    <td>
                      {formatQuantity(product.minimum_stock)}
                    </td>

                    <td>
                      {Number(product.units_to_minimum) > 0
                        ? formatQuantity(
                          product.units_to_minimum
                        )
                        : "—"}
                    </td>

                    <td>
                      {product.alert_status === "exhausted" ? (
                        <span className="badge-stock-exhausted">
                          Agotado
                        </span>
                      ) : product.alert_status === "critical" ? (
                        <span className="badge-stock-critical">
                          Crítico
                        </span>
                      ) : product.alert_status === "low" ? (
                        <span className="badge-low-stock">
                          Stock bajo
                        </span>
                      ) : (
                        <span className="badge-stock-ok">
                          Disponible
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          total={inventoryTotal}
          offset={inventoryOffset}
          onPageChange={setInventoryOffset}
        />
      </section>

      <section className="inventory-card inventory-lots-card">
        <div className="section-title">
          <div>
            <h2>Lotes disponibles y vencimientos</h2>
            <p>
              Consulta el saldo de cada lote y prioriza los productos
              próximos a vencer.
            </p>
          </div>

          <span className="stock-alert-counter">
            {lotsTotal} lotes encontrados
          </span>
        </div>

        <div className="inventory-filters">
          <select
            value={lotExpirationFilter}
            onChange={(event) => {
              setLotExpirationFilter(event.target.value);
              setLotsOffset(0);
            }}
          >
            <option value="">Todos los lotes</option>
            <option value="expired">Vencidos</option>
            <option value="expiring_soon">
              Próximos a vencer
            </option>
            <option value="valid">Vigentes</option>
            <option value="without_expiration">
              Sin vencimiento
            </option>
          </select>

          {lotExpirationFilter && (
            <button
              type="button"
              className="btn-clear-filters"
              onClick={() => {
                setLotExpirationFilter("");
                setLotsOffset(0);
              }}
            >
              Limpiar filtro
            </button>
          )}
        </div>

        <div className="inventory-table-wrapper">
          <table className="inventory-table inventory-lots-table">
            <thead>
              <tr>
                <th>Producto</th>
                <th>Bodega</th>
                <th>Lote</th>
                <th>Vencimiento</th>
                <th>Días</th>
                <th>Disponible</th>
                <th>Costo unitario</th>
                <th>Estado</th>
              </tr>
            </thead>

            <tbody>
              {lots.length === 0 ? (
                <tr>
                  <td colSpan="8" className="empty-inventory">
                    No hay lotes para los filtros seleccionados.
                  </td>
                </tr>
              ) : (
                lots.map((lot) => (
                  <tr
                    key={lot.id}
                    className={
                      lot.expiration_status === "expired"
                        ? "lot-expired-row"
                        : lot.expiration_status === "expiring_soon"
                          ? "lot-expiring-row"
                          : ""
                    }
                  >
                    <td>
                      <strong>{lot.product_name}</strong>
                      <small>{lot.product_sku}</small>
                    </td>
                    <td>{lot.warehouse_name || "Sin bodega"}</td>

                    <td>{lot.lot_number}</td>
                    <td>{formatDate(lot.expiration_date)}</td>

                    <td>
                      {lot.days_to_expiration === null ? (
                        "—"
                      ) : lot.days_to_expiration < 0 ? (
                        `${Math.abs(
                          lot.days_to_expiration
                        )} vencidos`
                      ) : (
                        `${lot.days_to_expiration} días`
                      )}
                    </td>

                    <td>
                      {formatQuantity(lot.available_quantity)}
                    </td>

                    <td>{formatMoney(lot.unit_cost)}</td>

                    <td>
                      <span
                        className={`lot-status ${lot.expiration_status}`}
                      >
                        {getLotStatusLabel(
                          lot.expiration_status
                        )}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          total={lotsTotal}
          offset={lotsOffset}
          onPageChange={setLotsOffset}
        />
      </section>

      <section className="inventory-card movements-history-card">
        <div className="section-title">
          <div>
            <h2>Historial de movimientos</h2>
            <p>
              Filtra por producto, tipo de movimiento o rango de
              fechas.
            </p>
          </div>
        </div>

        <div className="inventory-movement-filters">
          <input
            type="search"
            placeholder="Buscar producto o SKU"
            value={movementSearch}
            onChange={(event) => {
              setMovementSearch(event.target.value);
              setMovementsOffset(0);
            }}
          />

          <select
            value={movementProductFilter}
            onChange={(event) => {
              setMovementProductFilter(event.target.value);
              setMovementsOffset(0);
            }}
          >
            <option value="">Todos los productos</option>

            {productOptions.map((product) => (
              <option key={product.id} value={product.id}>
                {product.sku} - {product.name}
              </option>
            ))}
          </select>

          <select
            value={movementTypeFilter}
            onChange={(event) => {
              setMovementTypeFilter(event.target.value);
              setMovementsOffset(0);
            }}
          >
            <option value="">Entradas y salidas</option>
            <option value="entry">Entradas</option>
            <option value="exit">Salidas</option>
          </select>

          <input
            type="date"
            value={dateFrom}
            onChange={(event) => {
              setDateFrom(event.target.value);
              setMovementsOffset(0);
            }}
          />

          <input
            type="date"
            value={dateTo}
            onChange={(event) => {
              setDateTo(event.target.value);
              setMovementsOffset(0);
            }}
          />

          <button
            type="button"
            className="btn-clear-filters"
            onClick={resetMovementFilters}
          >
            Limpiar filtros
          </button>
        </div>

        {movements.length === 0 ? (
          <p className="empty-movements">
            No hay movimientos para los filtros seleccionados.
          </p>
        ) : (
          <div className="inventory-table-wrapper">
            <table className="inventory-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Producto</th>
                  <th>Bodega</th>
                  <th>Tipo</th>
                  <th>Concepto</th>
                  <th>Cantidad</th>
                </tr>
              </thead>

              <tbody>
                {movements.map((movement) => (
                  <tr key={movement.id}>
                    <td>
                      {new Date(
                        movement.created_at
                      ).toLocaleString("es-CO")}
                    </td>

                    <td>
                      {movement.variant_sku ? (
                        <>
                          <strong>
                            {movement.variant_sku}
                          </strong>
                          <br />
                          <small>
                            {movement.product_name || "Producto"} ·{" "}
                            {movement.variant_name || "Variante"}
                          </small>
                        </>
                      ) : (
                        movement.product_name ||
                        productNames[movement.product_id] ||
                        `Producto #${movement.product_id}`
                      )}
                    </td>

                    <td>{movement.warehouse_name || "Sin bodega"}</td>

                    <td>
                      <span
                        className={
                          movement.movement_type === "entry"
                            ? "movement-entry"
                            : "movement-exit"
                        }
                      >
                        {movement.movement_type === "entry"
                          ? "Entrada"
                          : "Salida"}
                      </span>
                    </td>

                    <td>{movement.concept}</td>

                    <td
                      className={
                        movement.movement_type === "entry"
                          ? "quantity-entry"
                          : "quantity-exit"
                      }
                    >
                      {movement.movement_type === "entry"
                        ? "+"
                        : "-"}
                      {formatQuantity(movement.quantity)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination
          total={movementsTotal}
          offset={movementsOffset}
          onPageChange={setMovementsOffset}
        />
      </section>
    </div>
  );
}

export default CompanyInventory;